<template>
  <form @submit.prevent="editSubmit" name="event-form" id="form-event" class="task-form-modern">
    <section class="task-shell">
      <header class="task-hero">
        <div class="task-hero-main">
          <span class="task-kicker">Acompanhamento do chamado</span>
          <h4 class="task-hero-title">
            <span class="task-id">#{{ sendData.id || "-" }}</span>
            {{ sendData.tarefa || "Chamado sem título" }}
          </h4>
          <p class="task-hero-subtitle">
            Aberto em {{ formatarDataCurta(sendData.createdAt) }}
            <template v-if="categoriaChamado"> · {{ categoriaChamado }}</template>
            <template v-if="sendData.localidade_resolvida"> · {{ sendData.localidade_resolvida }}</template>
          </p>

          <div class="task-pills">
            <span class="task-pill" :class="statusBadgeClass(sendData.status)">
              <i class="ri-flag-2-line"></i>{{ sendData.status || "Sem status" }}
            </span>
            <span class="task-pill is-neutral">
              <i class="ri-fire-line"></i>{{ sendData.prioridade || "Sem prioridade" }}
            </span>
            <span class="task-pill is-primary">
              <i class="ri-chat-3-line"></i>{{ sendData?.TarefaComentario?.length || 0 }} interação(ões)
            </span>
            <span v-if="cancelamento" class="task-pill is-danger">
              <i class="ri-close-circle-line"></i>Cancelado por você
            </span>
          </div>
        </div>

        <div class="task-hero-actions">
          <button
            v-if="sendData.id && isStatusEditavel(sendData.status)"
            type="submit"
            class="btn btn-primary"
            id="btn-save-event-top"
          >
            <i class="ri-send-plane-line me-1"></i>Atualizar chamado
          </button>
        </div>
      </header>

      <div class="task-grid">
        <div class="task-main">
          <div class="task-description-card">
            <label class="form-label mb-2">Descrição inicial</label>
            <div class="task-description-content conteudo-rico" v-html="descricaoSegura"></div>
          </div>

          <div class="conversation-wrap">
            <div class="conversation-header">
              <h6 class="mb-0">Histórico de interações</h6>
              <small class="text-muted">{{ sendData?.TarefaComentario?.length || 0 }} mensagens</small>
            </div>

            <div class="conversation-list" v-if="sendData.id" @click="ampliarImagem">
              <template v-if="comentariosAgrupados.length > 0">
                <template v-for="(grupo, grupoIndex) in comentariosAgrupados" :key="`grupo-comentarios-${grupo.key}-${grupoIndex}`">
                  <div class="conversation-day-divider">
                    <span class="badge conversation-day-badge">{{ grupo.label }}</span>
                  </div>
                  <div
                    v-for="(option, index) in grupo.items"
                    :key="option.cId || `${grupo.key}-${index}`"
                    class="conversation-row"
                    :class="{ 'is-self': comentarioEhDoUsuario(option) }"
                  >
                    <div class="conversation-bubble">
                      <div class="conversation-meta">
                        <strong>{{ comentarioEhDoUsuario(option) ? "Você" : option?.TarefaUsuario?.nome || "Atendente" }}</strong>
                        <span>{{ formatarHoraComentario(option.createdAt) }}</span>
                      </div>
                      <div class="conversation-text conteudo-rico" v-html="sanitizarComentario(option.comentario)"></div>
                    </div>
                  </div>
                </template>
              </template>
              <div v-else class="conversation-empty">Ainda não existem comentários neste chamado.</div>
            </div>
          </div>

          <!-- Fotos: sempre visiveis (mesmo com o chamado fechado), independente
               de poder comentar ou nao. -->
          <div v-if="sendData.id" class="composer-card">
            <div class="composer-head">
              <h6 class="mb-1">Fotos do chamado</h6>
              <p class="text-muted mb-0">Anexe fotos para ilustrar o problema ou complementar o atendimento.</p>
            </div>
            <FotosChamado ref="fotosChamado" :tarefa-id="sendData.id" :usuario-id="0" />
          </div>

          <div v-if="isStatusEditavel(sendData.status)" class="composer-card">
            <div class="composer-head">
              <h6 class="mb-1">Adicionar comentário</h6>
              <p class="text-muted mb-0">
                <span class="fw-semibold text-primary">{{ user.nome }}</span>, descreva o andamento para agilizar o atendimento.
              </p>
            </div>
            <textarea
              v-model="comentario"
              rows="6"
              class="form-control composer-textarea"
              placeholder="Ex.: Validado com o usuário, problema persiste após reinicialização..."
            ></textarea>
          </div>

          <!-- Cancelamento pelo solicitante: só enquanto ninguém do atendimento
               interagiu. Depois disso o chamado já consumiu trabalho e encerrar
               passa a ser decisão da equipe. -->
          <div v-if="cancelamento" class="cancel-card is-done">
            <div class="composer-head">
              <h6 class="mb-1"><i class="ri-shield-check-line me-1"></i>Chamado cancelado por você</h6>
              <p class="text-muted mb-0">
                Cancelamento assinado eletronicamente em {{ formatarDataHoraCurta(cancelamento.cancelado_em) }}.
              </p>
            </div>
            <div class="cancel-justificativa">{{ cancelamento.justificativa }}</div>
            <div class="cancel-assinatura">
              <div>
                <span>Código de verificação</span>
                <code>{{ (cancelamento.assinatura_hash || "").slice(0, 24) }}</code>
              </div>
              <a class="btn btn-sm btn-soft-secondary" :href="urlComprovante" target="_blank" rel="noopener">
                <i class="ri-file-pdf-2-line me-1"></i>Comprovante
              </a>
            </div>
          </div>

          <div v-else-if="podeCancelar" class="cancel-card">
            <div class="composer-head">
              <h6 class="mb-1"><i class="ri-close-circle-line me-1"></i>Cancelar este chamado</h6>
              <p class="text-muted mb-0">
                Nenhum técnico interagiu com o chamado ainda, então você mesmo pode cancelá-lo.
                Descreva o motivo — ele fica registrado no histórico.
              </p>
            </div>
            <textarea
              v-model="justificativaCancelamento"
              rows="3"
              class="form-control"
              maxlength="4000"
              placeholder="Ex.: Resolvido por conta própria; abri o chamado por engano; o equipamento foi substituído..."
            ></textarea>
            <div class="cancel-aviso">
              <i class="ri-information-line me-1"></i>
              Ao confirmar, o cancelamento é assinado eletronicamente em seu nome, com data, hora e
              endereço de origem registrados. Você receberá um comprovante em PDF.
            </div>
            <div class="cancel-acoes">
              <span class="text-muted small">{{ justificativaCancelamento.trim().length }}/15 caracteres mínimos</span>
              <button
                type="button"
                class="btn btn-danger"
                :disabled="cancelando || justificativaCancelamento.trim().length < 15"
                @click="cancelarChamado"
              >
                <span v-if="cancelando" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="ri-close-circle-line me-1"></i>
                Cancelar chamado
              </button>
            </div>
          </div>

          <footer class="task-footer">
            <button
              v-if="isStatusEditavel(sendData.status)"
              type="submit"
              class="btn btn-primary"
              id="btn-save-event"
            >
              <i class="ri-send-plane-line me-1"></i>Atualizar chamado
            </button>
          </footer>
        </div>

        <aside class="task-side">
          <div class="side-card">
            <div class="side-card-head">
              <span class="side-kicker">Resumo</span>
              <h6 class="mb-0">Dados do chamado</h6>
            </div>
            <div class="side-summary">
              <div v-for="campo in resumoCampos" :key="campo.rotulo" class="side-summary-row">
                <span>{{ campo.rotulo }}</span>
                <strong>{{ campo.valor }}</strong>
              </div>
            </div>
          </div>

          <div class="side-card">
            <div class="side-card-head">
              <span class="side-kicker">Andamento</span>
              <h6 class="mb-0">Movimentações</h6>
            </div>

            <div v-if="carregandoHistorico" class="side-loading">
              <span class="spinner-border spinner-border-sm me-2"></span>Carregando...
            </div>

            <ol v-else-if="movimentacoesRelevantes.length > 0" class="side-timeline">
              <li v-for="(item, index) in movimentacoesRelevantes" :key="`mov-${index}`" class="side-timeline-item">
                <span class="side-timeline-dot" :class="item.tom"></span>
                <div class="side-timeline-body">
                  <strong>{{ item.titulo }}</strong>
                  <p v-if="item.detalhe" class="mb-0">{{ item.detalhe }}</p>
                  <small>{{ item.quando }}<template v-if="item.autor"> · {{ item.autor }}</template></small>
                </div>
              </li>
            </ol>

            <div v-else class="side-empty">Nenhuma movimentação registrada até o momento.</div>
          </div>
        </aside>
      </div>
    </section>
  </form>

  <!-- Mesmo visualizador dos anexos: a imagem da conversa e o anexo do chamado
       abrem com a mesma janela e as mesmas ações. -->
  <VisualizadorAnexo
    v-model:visible="modalImagemShow"
    :url="imagemAmpliada"
    nome="Imagem do comentário"
    tipo="imagem"
    :permite-baixar="Boolean(imagemAmpliada)"
    @close="modalImagemShow = false"
    @baixar="baixarImagemAmpliada"
  />

  <BModal
    v-model="modalShow"
    title="Nova subtarefa"
    header-close-class="text-white bg-white"
    title-class="text-white font-18"
    body-class="p-4"
    header-class="p-3 bg-primary"
    hide-footer
    class="v-modal-custom"
    centered
  >
    <div class="row g-3">
      <div class="col-12">
        <label class="form-label">Tarefa</label>
        <input v-model="subtask.nome" type="text" class="form-control" />
      </div>
      <div class="col-12">
        <label class="form-label">Descricao</label>
        <textarea v-model="subtask.descricao" type="text" class="form-control" />
      </div>
      <div class="col-12 d-flex justify-content-end">
        <button @click.prevent="createSubtask" type="button" class="btn btn-primary" id="btn-save-subtask">
          Criar tarefa
        </button>
      </div>
    </div>
  </BModal>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.form-control {
  border: 1px solid #c8c8c8;
  color: #111827;
}

[data-bs-theme="dark"] .form-control {
  border: 1px solid #3c3f42;
  color: #ced4da;
}

.multiselect.form-control .multiselect__input,
.multiselect.form-control .multiselect__single {
  background: inherit;
  color: inherit;
}

.multiselect.form-control.multiselect--active .multiselect__tags {
  box-shadow: none;
}

.multiselect.form-control .multiselect__content-wrapper {
  background: var(--bs-body-bg, white);
  color: var(--bs-body-color, black);
  border-color: var(--bs-border-color, #c8c8c8);
}

.multiselect-wrapper .multiselect__tags {
  background: inherit;
  color: inherit;
  display: block;
  width: 100%;
  font-size: 0.825rem;
  font-weight: 400;
  line-height: 1.5;
  border: none;
}

.multiselect-wrapper .multiselect__input,
.multiselect-wrapper .multiselect__single {
  font-size: 0.825rem;
  margin-bottom: 0.125rem;
  background: inherit;
  color: inherit;
  border: inherit;
}

.task-form-modern {
  --task-text: #1f2937;
  --task-title: #0f172a;
  --task-muted: #64748b;
  --task-card-bg: rgba(255, 255, 255, 0.98);
  --task-card-border: #d9dee7;
  --task-card-shadow: 0 12px 24px rgba(15, 23, 42, 0.04);
  --task-header-bg: linear-gradient(135deg, rgba(11, 94, 215, 0.12), rgba(11, 94, 215, 0.03));
  --task-header-border: rgba(11, 94, 215, 0.18);
  --task-day-badge-bg: #ffffff;
  --task-day-badge-border: #dbe4f0;
  --task-day-badge-text: #64748b;
  --task-bubble-bg: #f8fafc;
  --task-bubble-border: #e2e8f0;
  --task-bubble-self-bg: #e0edff;
  --task-bubble-self-border: #bfd8ff;
  --task-empty-bg: #f8fafc;
  --task-empty-border: #cbd5e1;
  --task-hero-bg: linear-gradient(135deg, #eef8f7 0%, #edf4fb 100%);
  --task-hero-border: rgba(148, 163, 184, 0.22);
  --task-pill-bg: rgba(255, 255, 255, 0.92);
  --task-pill-border: rgba(100, 116, 139, 0.18);
  --task-pill-text: #334155;
  color: var(--bs-body-color, #1f2937);
}

[data-bs-theme="dark"] .task-form-modern,
[data-layout-mode="dark"] .task-form-modern {
  --task-text: #dbe7f4;
  --task-title: #eff6ff;
  --task-muted: #9cb0cb;
  --task-card-bg: rgba(11, 19, 34, 0.78);
  --task-card-border: rgba(148, 163, 184, 0.16);
  --task-card-shadow: 0 18px 32px rgba(2, 8, 20, 0.2);
  --task-header-bg: linear-gradient(135deg, rgba(30, 64, 175, 0.24), rgba(15, 23, 42, 0.92));
  --task-header-border: rgba(96, 165, 250, 0.26);
  --task-day-badge-bg: rgba(15, 23, 42, 0.82);
  --task-day-badge-border: rgba(148, 163, 184, 0.18);
  --task-day-badge-text: #aac0dc;
  --task-bubble-bg: rgba(15, 23, 42, 0.84);
  --task-bubble-border: rgba(148, 163, 184, 0.16);
  --task-bubble-self-bg: rgba(30, 64, 175, 0.24);
  --task-bubble-self-border: rgba(96, 165, 250, 0.28);
  --task-empty-bg: rgba(15, 23, 42, 0.62);
  --task-empty-border: rgba(148, 163, 184, 0.2);
  --task-hero-bg: linear-gradient(135deg, #1a2535 0%, #182230 100%);
  --task-hero-border: rgba(148, 163, 184, 0.2);
  --task-pill-bg: rgba(15, 23, 42, 0.78);
  --task-pill-border: rgba(148, 163, 184, 0.2);
  --task-pill-text: #e2e8f0;
  color: var(--task-text);
}

.task-shell {
  display: grid;
  gap: 1rem;
}

/* Hero claro no padrão do projeto: identifica o chamado e o estado dele antes
   de qualquer conteúdo, sem competir com a conversa. */
.task-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.5rem;
  border: 1px solid var(--task-hero-border);
  border-radius: 1.1rem;
  background: var(--task-hero-bg);
}

.task-hero-main {
  flex: 1 1 320px;
  min-width: 0;
}

.task-hero-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0.4rem 0 0.25rem;
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--task-title);
}

.task-hero-subtitle {
  margin: 0;
  font-size: 0.84rem;
  color: var(--task-muted);
}

.task-hero-actions {
  flex: 0 0 auto;
}

.task-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.9rem;
}

.task-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--task-pill-border);
  background: var(--task-pill-bg);
  color: var(--task-pill-text);
  font-size: 0.76rem;
  font-weight: 700;
}

.task-pill.is-neutral {
  background: var(--task-pill-bg);
}

.task-pill.is-primary {
  background: rgba(14, 165, 233, 0.14);
  border-color: rgba(14, 165, 233, 0.2);
  color: #0369a1;
}

.task-pill.is-danger {
  background: rgba(239, 68, 68, 0.14);
  border-color: rgba(239, 68, 68, 0.22);
  color: #b91c1c;
}

/* Corpo em duas colunas: conversa à esquerda, contexto à direita. Colapsa numa
   coluna no tablet para baixo, onde a leitura empilhada é melhor. */
.task-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.75fr) minmax(280px, 0.9fr);
  gap: 1rem;
  align-items: start;
}

.task-main,
.task-side {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.task-side {
  position: sticky;
  top: 0.5rem;
}

.side-card {
  padding: 1.1rem 1.15rem;
  background: var(--task-card-bg);
  border: 1px solid var(--task-card-border);
  border-radius: 1rem;
  box-shadow: var(--task-card-shadow);
}

.side-card-head {
  margin-bottom: 0.9rem;
}

.side-kicker {
  display: block;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0b5ed7;
  margin-bottom: 0.15rem;
}

.side-card-head h6 {
  font-weight: 800;
  color: var(--task-title);
}

.side-summary {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.side-summary-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--task-card-border);
}

.side-summary-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.side-summary-row span {
  color: var(--task-muted);
  font-size: 0.78rem;
}

.side-summary-row strong {
  color: var(--task-title);
  font-size: 0.82rem;
  text-align: right;
  word-break: break-word;
}

.side-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.side-timeline-item {
  display: flex;
  gap: 0.6rem;
}

.side-timeline-dot {
  flex: 0 0 auto;
  width: 0.6rem;
  height: 0.6rem;
  margin-top: 0.32rem;
  border-radius: 999px;
  background: #94a3b8;
}

.side-timeline-dot.is-primary { background: #0b5ed7; }
.side-timeline-dot.is-success { background: #16a34a; }
.side-timeline-dot.is-info { background: #0ea5e9; }
.side-timeline-dot.is-neutral { background: #94a3b8; }

.side-timeline-body {
  min-width: 0;
}

.side-timeline-body strong {
  display: block;
  font-size: 0.83rem;
  color: var(--task-title);
}

.side-timeline-body p {
  font-size: 0.78rem;
  color: var(--task-text);
  word-break: break-word;
}

.side-timeline-body small {
  font-size: 0.72rem;
  color: var(--task-muted);
}

.side-loading,
.side-empty {
  font-size: 0.82rem;
  color: var(--task-muted);
}

/* Cancelamento pelo solicitante. */
.cancel-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.15rem 1.25rem;
  border: 1px solid rgba(239, 68, 68, 0.26);
  border-radius: 1rem;
  background: rgba(239, 68, 68, 0.06);
}

.cancel-card.is-done {
  border-color: rgba(100, 116, 139, 0.28);
  background: var(--task-empty-bg);
}

.cancel-card .composer-head h6 {
  color: #b91c1c;
  font-weight: 800;
}

.cancel-card.is-done .composer-head h6 {
  color: var(--task-title);
}

.cancel-aviso {
  font-size: 0.78rem;
  color: var(--task-muted);
}

.cancel-acoes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.cancel-justificativa {
  padding: 0.85rem 1rem;
  border: 1px solid var(--task-card-border);
  border-radius: 0.85rem;
  background: var(--task-card-bg);
  font-size: 0.88rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.cancel-assinatura {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.cancel-assinatura span {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--task-muted);
}

.cancel-assinatura code {
  font-size: 0.78rem;
  word-break: break-all;
}

@media (max-width: 1199.98px) {
  .task-grid {
    grid-template-columns: 1fr;
  }

  .task-side {
    position: static;
  }
}

@media (max-width: 575.98px) {
  .task-hero {
    padding: 1.1rem;
  }

  .task-hero-actions,
  .task-hero-actions .btn {
    width: 100%;
  }
}

.task-header {
  align-items: center;
  background: var(--task-header-bg);
  border: 1px solid var(--task-header-border);
  border-radius: 1rem;
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.25rem;
}

.task-kicker {
  color: #0b5ed7;
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.task-kicker-row {
  align-items: center;
  display: inline-flex;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.task-title {
  align-items: center;
  color: var(--task-title);
  display: flex;
  flex-wrap: wrap;
  font-weight: 700;
  gap: 0.45rem;
}

.task-id {
  background: #0b5ed7;
  border-radius: 999px;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
}

.task-status-pill {
  align-items: center;
  border-radius: 0.55rem;
  display: inline-flex;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.15;
  min-height: 1.35rem;
  padding: 0.16rem 0.5rem;
  text-transform: none;
  white-space: nowrap;
}

.status-new {
  background: #14532d;
  border: 1px solid #166534;
  color: #dcfce7;
}

.status-pending {
  background: #7c2d12;
  border: 1px solid #9a3412;
  color: #ffedd5;
}

.status-active {
  background: #0c4a6e;
  border: 1px solid #075985;
  color: #e0f2fe;
}

.status-solved {
  background: #4c1d95;
  border: 1px solid #5b21b6;
  color: #ede9fe;
}

.status-finished {
  background: #1e3a8a;
  border: 1px solid #1e40af;
  color: #dbeafe;
}

.task-description-card {
  background: var(--task-card-bg);
  border: 1px solid var(--task-card-border);
  border-radius: 1rem;
  box-shadow: var(--task-card-shadow);
  padding: 1rem 1.15rem;
}

.task-description-content {
  color: var(--task-text);
  line-height: 1.55;
}

.conversation-wrap {
  background: var(--task-card-bg);
  border: 1px solid var(--task-card-border);
  border-radius: 1rem;
  box-shadow: var(--task-card-shadow);
  padding: 1rem;
}

.conversation-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.85rem;
}

.conversation-list {
  display: grid;
  gap: 0.7rem;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 0.3rem;
}

.conversation-day-divider {
  margin: 0.2rem 0;
  text-align: center;
}

.conversation-day-badge {
  background: var(--task-day-badge-bg);
  border: 1px solid var(--task-day-badge-border);
  border-radius: 999px;
  color: var(--task-day-badge-text);
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.28rem 0.65rem;
}

.conversation-row {
  display: flex;
  justify-content: flex-start;
}

.conversation-row.is-self {
  justify-content: flex-end;
}

.conversation-bubble {
  background: var(--task-bubble-bg);
  border: 1px solid var(--task-bubble-border);
  border-radius: 0.9rem;
  max-width: min(760px, 92%);
  padding: 0.75rem 0.9rem;
}

.conversation-row.is-self .conversation-bubble {
  background: var(--task-bubble-self-bg);
  border-color: var(--task-bubble-self-border);
}

.conversation-meta {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.78rem;
  gap: 0.5rem;
  margin-bottom: 0.45rem;
}

.conversation-meta span {
  color: var(--task-muted);
}

.conversation-text {
  color: var(--task-title);
  font-size: 0.92rem;
  line-height: 1.5;
}

/* Conteúdo vindo do editor rico do atendimento (descrição e comentários).
   A bolha é estreita, então a imagem ganha teto de altura e abre ampliada no
   clique. As variáveis --task-* já cobrem claro e escuro. */
.conteudo-rico {
  overflow-wrap: anywhere;
}

.conteudo-rico p {
  margin-bottom: 0.35rem;
}

.conteudo-rico p:last-child {
  margin-bottom: 0;
}

.conteudo-rico ul,
.conteudo-rico ol {
  margin-bottom: 0.35rem;
  padding-left: 1.25rem;
}

.conteudo-rico img {
  display: block;
  max-width: 100%;
  max-height: 240px;
  width: auto;
  height: auto;
  margin: 0.4rem 0;
  border: 1px solid var(--task-card-border);
  border-radius: 0.5rem;
  cursor: zoom-in;
  object-fit: contain;
}

.conteudo-rico figure {
  margin: 0.4rem 0;
}

.conteudo-rico figcaption {
  color: var(--task-muted);
  font-size: 0.72rem;
}

.conteudo-rico pre {
  white-space: pre-wrap;
  padding: 0.5rem;
  border-radius: 0.4rem;
  background: var(--task-empty-bg);
  font-size: 0.78rem;
}

.conteudo-rico table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.conteudo-rico td,
.conteudo-rico th {
  border: 1px solid var(--task-card-border);
  padding: 0.25rem 0.4rem;
}


.conversation-empty {
  background: var(--task-empty-bg);
  border: 1px dashed var(--task-empty-border);
  border-radius: 0.85rem;
  color: var(--task-muted);
  font-size: 0.9rem;
  padding: 1rem;
  text-align: center;
}

.composer-card {
  background: var(--task-card-bg);
  border: 1px solid var(--task-card-border);
  border-radius: 1rem;
  box-shadow: var(--task-card-shadow);
  padding: 1rem;
}

.composer-head {
  margin-bottom: 0.8rem;
}

.composer-textarea {
  min-height: 160px;
  resize: vertical;
}

.task-form-modern .text-muted {
  color: var(--task-muted) !important;
}

.task-form-modern .form-label {
  color: var(--task-title);
}

.task-description-content em {
  color: var(--task-muted);
}

.task-form-modern .form-control,
.v-modal-custom .form-control {
  background: #ffffff;
}

[data-bs-theme="dark"] .task-form-modern .form-control,
[data-bs-theme="dark"] .v-modal-custom .form-control {
  background: rgba(8, 15, 28, 0.86);
  border-color: rgba(148, 163, 184, 0.2) !important;
  color: #dbe7f4;
}

[data-bs-theme="dark"] .task-form-modern .form-control::placeholder,
[data-bs-theme="dark"] .v-modal-custom .form-control::placeholder {
  color: #7f95b0;
}

.task-form-modern .conversation-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.35) transparent;
}

[data-bs-theme="dark"] .task-form-modern .btn.btn-primary {
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.2);
}

[data-bs-theme="dark"] .v-modal-custom .modal-content {
  background: linear-gradient(180deg, rgba(8, 15, 28, 0.98) 0%, rgba(13, 23, 40, 0.96) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  color: #dbe7f4;
  box-shadow: 0 24px 42px rgba(2, 8, 20, 0.34);
}

[data-bs-theme="dark"] .v-modal-custom .modal-body {
  color: #dbe7f4;
}

[data-bs-theme="dark"] .v-modal-custom .form-label {
  color: #eff6ff;
}

[data-bs-theme="dark"] .v-modal-custom .btn-close {
  filter: invert(1) grayscale(1) brightness(200%);
  opacity: 0.9;
}

.task-footer {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 991.98px) {
  .task-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.8rem;
  }

  .task-header .btn {
    width: 100%;
  }

  .task-footer .btn {
    width: 100%;
  }
}
</style>
<script>
import axios from "axios";
import Swal from "sweetalert2";
import { confirmarAsync, erro, sucesso } from "@/helpers/mirai/custom";
import { sanitizarHtml } from "@/helpers/mirai/html";
import { categories } from "../utils";
import Portuguese from "flatpickr/dist/l10n/pt.js";
import moment from "moment";
import "@ckeditor/ckeditor5-build-classic/build/translations/pt-br";
import CustomEditor from "@/ckeditor.js";
import FotosChamado from "@/views/mirai/dashboard/components/FotosChamado.vue";
import VisualizadorAnexo from "@/components/mirai/VisualizadorAnexo.vue";

export default {
  name: "formTarefa",
  emits: ["createEvent", "updateEvent", "deleteEvent", "canceladoEvent"],
  components: {
    FotosChamado,
    VisualizadorAnexo,
  },
  props: {
    data: {
      type: Object,
    },
  },
  computed: {
    moment() {
      return moment;
    },
    // Descrição e comentários vêm do editor rico do atendimento e são exibidos
    // com v-html — passam pela allowlist antes de ir para a tela.
    descricaoSegura() {
      const html = sanitizarHtml(this.sendData?.obs || "");
      return html || "<em>Sem descricao.</em>";
    },
    comentariosOrdenados() {
      const lista = Array.isArray(this.sendData?.TarefaComentario) ? [...this.sendData.TarefaComentario] : [];
      return lista.sort((a, b) => this.obterTimestampComentario(b) - this.obterTimestampComentario(a));
    },
    comentariosAgrupados() {
      const lista = Array.isArray(this.comentariosOrdenados) ? this.comentariosOrdenados : [];
      if (!lista.length) return [];

      const grupos = [];
      const mapa = new Map();

      lista.forEach((comentario) => {
        const infoDia = this.obterInfoDiaComentario(comentario);
        if (!mapa.has(infoDia.key)) {
          const grupo = {
            key: infoDia.key,
            label: infoDia.label,
            items: [],
          };
          mapa.set(infoDia.key, grupo);
          grupos.push(grupo);
        }
        mapa.get(infoDia.key).items.push(comentario);
      });

      return grupos;
    },
    categoriaChamado() {
      return this.sendData?.Categoria?.nome_nivelado || this.sendData?.Categoria?.nome || null;
    },
    resumoCampos() {
      const atribuicoes = Array.isArray(this.sendData?.TarefaAtribuido) ? this.sendData.TarefaAtribuido : [];
      const fila = atribuicoes
        .map((item) => item?.Setor?.nome || item?.Grupo?.nome || item?.Usuario?.nome)
        .filter(Boolean)
        .join(", ");

      return [
        { rotulo: "Número", valor: `#${this.sendData?.id || "-"}` },
        { rotulo: "Situação", valor: this.sendData?.status || "—" },
        { rotulo: "Prioridade", valor: this.sendData?.prioridade || "—" },
        { rotulo: "Categoria", valor: this.categoriaChamado || "—" },
        { rotulo: "Fila responsável", valor: fila || "Aguardando triagem" },
        { rotulo: "Aberto em", valor: this.formatarDataHoraCurta(this.sendData?.createdAt) },
        { rotulo: "Última atualização", valor: this.formatarDataHoraCurta(this.sendData?.updatedAt) },
        { rotulo: "Interações", valor: String(this.sendData?.TarefaComentario?.length || 0) },
      ];
    },
    /**
     * Movimentações que interessam a quem abriu o chamado.
     *
     * Edição de campos internos e comentários ficam de fora: comentário já
     * aparece na conversa, e detalhe operacional aqui vira ruído para o
     * solicitante.
     */
    movimentacoesRelevantes() {
      const relevantes = new Set(["criacao", "situacao", "transferencia", "solucao", "encerramento", "anexo"]);
      const tons = {
        criacao: "is-info",
        situacao: "is-primary",
        transferencia: "is-info",
        solucao: "is-success",
        encerramento: "is-neutral",
        anexo: "is-neutral",
      };

      return (Array.isArray(this.movimentacoes) ? this.movimentacoes : [])
        .filter((item) => relevantes.has(String(item?.acao || "")))
        .slice(0, 12)
        .map((item) => {
          const mudancaStatus = (Array.isArray(item?.alteracoes) ? item.alteracoes : []).find(
            (alteracao) => alteracao?.campo === "status"
          );
          const detalhe = mudancaStatus
            ? `${mudancaStatus.de_rotulo || mudancaStatus.de || "—"} → ${mudancaStatus.para_rotulo || mudancaStatus.para || "—"}`
            : (item?.descricao && item.descricao !== item.acao_rotulo ? item.descricao : null);

          const login = String(item?.usuario_login || "").trim();
          const souEu = login && this.user?.usuario && login === this.user.usuario;

          return {
            titulo: item?.acao_rotulo || item?.acao || "Movimentação",
            detalhe: detalhe ? String(detalhe).slice(0, 180) : null,
            quando: this.formatarDataHoraCurta(item?.created_at),
            autor: souEu ? "Você" : item?.usuario_nome || null,
            tom: tons[item?.acao] || "is-neutral",
          };
        });
    },
    urlComprovante() {
      const base = axios.defaults.baseURL || "";
      return `${base.replace(/\/+$/, "")}/tarefa/cancelamento?tarefa=${this.sendData?.id}&formato=pdf`;
    },
  },
  data() {
    return {
      layout: false,
      solicitantes: [],
      atribuidos: [],
      isLoading: false,
      editor: CustomEditor,
      config: {
        language: "pt-br",
      },
      editorData: "",
      subtask: {
        nome: "",
        descricao: "",
      },
      modalShow: false,
      modalImagemShow: false,
      imagemAmpliada: null,
      dateTimeConfig: {
        locale: Portuguese.pt,
        enableTime: true,
        dateFormat: "Y-m-d H:i:S",
        altInput: true,
        altFormat: "d/m/Y H:i",
      },
      user: JSON.parse(localStorage.getItem("userdata")),
      categories,
      comentario: "",
      subtarefas: [],
      comentarios: [],
      membros: [],
      isSolucionadaPromptOpen: false,
      modalElement: null,
      sendData: {},
      // Aside: movimentações relevantes (auditoria do chamado).
      movimentacoes: [],
      carregandoHistorico: false,
      // Cancelamento pelo solicitante.
      cancelamento: null,
      podeCancelar: false,
      motivoBloqueioCancelamento: null,
      justificativaCancelamento: "",
      cancelando: false,
    };
  },
  async mounted() {
    this.label = sessionStorage.getItem("tarefaForm") || false;
    this.$nextTick(() => {
      this.bindModalShownEvent();
    });
  },
  beforeUnmount() {
    if (this.modalElement) {
      this.modalElement.removeEventListener("shown.bs.modal", this.handleSolucionadaAviso);
    }
  },
  watch: {
    data: {
      handler() {
        this.initializeSendData();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    sanitizarComentario(html) {
      return sanitizarHtml(html);
    },
    // Print enviado pelo atendimento aparece reduzido na bolha; o clique abre
    // em tamanho cheio. Delegado no contêiner porque o conteúdo vem de v-html.
    ampliarImagem(event) {
      const alvo = event?.target;
      if (!alvo || alvo.tagName !== "IMG") return;
      const src = alvo.getAttribute("src");
      if (!src) return;
      this.imagemAmpliada = src;
      this.modalImagemShow = true;
    },
    // A imagem da conversa vem de URL já resolvida pelo navegador; o download
    // usa o próprio endereço, sem passar de novo pelo backend.
    baixarImagemAmpliada() {
      if (!this.imagemAmpliada) return;
      const link = document.createElement("a");
      link.href = this.imagemAmpliada;
      link.download = "imagem-do-chamado";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    statusBadgeClass(status) {
      const value = this.normalizeStatus(status);
      if (["finalizada", "concluida"].includes(value)) return "status-finished";
      if (value === "solucionada") return "status-solved";
      if (value === "pendente") return "status-pending";
      if (["em execucao", "em revisao"].includes(value)) return "status-active";
      return "status-new";
    },
    normalizeStatus(status) {
      return String(status || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    isStatusEditavel(status) {
      const value = this.normalizeStatus(status);
      return ["nova", "pendente", "em execucao", "em revisao"].includes(value);
    },
    bindModalShownEvent() {
      const modalEl = this.$el?.closest?.(".modal");
      if (!modalEl) return;
      this.modalElement = modalEl;
      this.modalElement.removeEventListener("shown.bs.modal", this.handleSolucionadaAviso);
      this.modalElement.addEventListener("shown.bs.modal", this.handleSolucionadaAviso);
    },
    getUltimoComentario() {
      if (!this.sendData?.TarefaComentario?.length) return null;
      return this.sendData.TarefaComentario[this.sendData.TarefaComentario.length - 1];
    },
    comentarioEhDoUsuario(comentario) {
      if (!comentario) return false;
      if (comentario?.TarefaUsuario?.id === 0) return true;
      return Boolean(comentario?.accountname && this.user?.usuario && comentario.accountname === this.user.usuario);
    },
    obterTimestampComentario(comentario) {
      const candidatos = [
        comentario?.createdAt,
        comentario?.dataCriacao,
        comentario?.data,
        comentario?.updatedAt,
      ];

      for (const valor of candidatos) {
        if (!valor) continue;
        const timestampMoment = moment(valor, [moment.ISO_8601, "YYYY-MM-DD HH:mm:ss", "DD/MM/YYYY HH:mm"], true).valueOf();
        if (Number.isFinite(timestampMoment)) return timestampMoment;
        const timestampDate = new Date(valor).getTime();
        if (Number.isFinite(timestampDate)) return timestampDate;
      }

      return 0;
    },
    formatarHoraComentario(valor) {
      const timestamp = this.obterTimestampComentario({
        createdAt: valor,
        dataCriacao: valor,
        data: valor,
        updatedAt: valor,
      });
      if (!Number.isFinite(timestamp) || timestamp <= 0) return "--:--";
      return moment(timestamp).format("HH:mm");
    },
    obterInicioDiaTimestamp(timestamp) {
      if (!Number.isFinite(timestamp) || timestamp <= 0) return 0;
      const data = new Date(timestamp);
      data.setHours(0, 0, 0, 0);
      return data.getTime();
    },
    obterRotuloDiaComentario(inicioDiaTimestamp) {
      if (!Number.isFinite(inicioDiaTimestamp) || inicioDiaTimestamp <= 0) return "Sem data";

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const ontem = new Date(hoje);
      ontem.setDate(ontem.getDate() - 1);

      if (inicioDiaTimestamp === hoje.getTime()) return "Hoje";
      if (inicioDiaTimestamp === ontem.getTime()) return "Ontem";
      return moment(inicioDiaTimestamp).format("DD/MM/YYYY");
    },
    obterInfoDiaComentario(comentario) {
      const timestamp = this.obterTimestampComentario(comentario);
      if (!Number.isFinite(timestamp) || timestamp <= 0) {
        return {
          key: "sem-data",
          label: "Sem data",
        };
      }

      const inicioDiaTimestamp = this.obterInicioDiaTimestamp(timestamp);
      return {
        key: `dia-${inicioDiaTimestamp}`,
        label: this.obterRotuloDiaComentario(inicioDiaTimestamp),
      };
    },
    async initializeSendData() {
      const idAnterior = this.sendData?.id || null;

      if (this.data && this.data.all) {
        this.sendData = JSON.parse(JSON.stringify(this.data.all));
      }
      if (this.sendData.TarefaComentario) {
        this.sendData.TarefaComentario.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      }
      this.handleSolucionadaAviso();

      // O watch é profundo e dispara a cada digitação no formulário; só vale
      // recarregar aside e cancelamento quando o chamado em tela muda.
      if (this.sendData?.id && this.sendData.id !== idAnterior) {
        this.justificativaCancelamento = "";
        this.carregarHistorico();
        this.carregarCancelamento();
      }
    },
    formatarDataCurta(valor) {
      if (!valor) return "—";
      const data = moment(valor);
      return data.isValid() ? data.format("DD/MM/YY") : "—";
    },
    formatarDataHoraCurta(valor) {
      if (!valor) return "—";
      const data = moment(valor);
      return data.isValid() ? data.format("DD/MM/YY HH:mm") : "—";
    },
    async carregarHistorico() {
      if (!this.sendData?.id) return;
      this.carregandoHistorico = true;
      try {
        const resposta = await axios.get("/tarefa/log", { params: { tarefa: this.sendData.id } });
        this.movimentacoes = Array.isArray(resposta.data?.registros) ? resposta.data.registros : [];
      } catch (e) {
        // Histórico é contexto: se falhar, o chamado continua utilizável.
        this.movimentacoes = [];
      } finally {
        this.carregandoHistorico = false;
      }
    },
    async carregarCancelamento() {
      if (!this.sendData?.id) return;
      try {
        const resposta = await axios.get("/tarefa/cancelamento", {
          params: { tarefa: this.sendData.id },
        });
        this.cancelamento = resposta.data?.cancelamento || null;
        this.podeCancelar = Boolean(resposta.data?.pode_cancelar);
        this.motivoBloqueioCancelamento = resposta.data?.motivo_bloqueio || null;
      } catch (e) {
        this.cancelamento = null;
        this.podeCancelar = false;
        this.motivoBloqueioCancelamento = null;
      }
    },
    async cancelarChamado() {
      const justificativa = this.justificativaCancelamento.trim();
      if (justificativa.length < 15) {
        erro({
          title: "Motivo muito curto",
          text: "Descreva o motivo do cancelamento com pelo menos 15 caracteres.",
        });
        return;
      }

      // `confirmarAsync` (e não o `confirmar` clássico, que trabalha por
      // callback e devolve `undefined`) para o fluxo continuar aqui mesmo.
      const confirmado = await confirmarAsync({
        title: "Cancelar este chamado?",
        text:
          "O cancelamento será assinado eletronicamente em seu nome e não poderá ser desfeito. " +
          "Se precisar do atendimento depois, será necessário abrir um novo chamado.",
        confirmButtonText: "Sim, cancelar chamado",
        cancelButtonText: "Voltar",
        variant: "danger",
      });
      if (!confirmado) return;

      this.cancelando = true;
      try {
        const resposta = await axios.post("/tarefa/cancelar", {
          tarefa_id: this.sendData.id,
          justificativa,
        });

        this.cancelamento = resposta.data?.cancelamento || null;
        this.podeCancelar = false;
        this.justificativaCancelamento = "";
        this.sendData.status = resposta.data?.status || "Cancelada";

        sucesso({
          title: "Chamado cancelado",
          text: "O comprovante assinado ficou disponível para download.",
        });

        // Evento próprio, não `updateEvent`: aquele dispara notificação de
        // "nova mensagem" para a fila e um segundo toast de sucesso — nenhum
        // dos dois faz sentido num cancelamento.
        this.$emit("canceladoEvent", { ...this.sendData });
        this.carregarHistorico();
      } catch (e) {
        erro({ text: e?.response?.data?.error || "Não foi possível cancelar o chamado." });
      } finally {
        this.cancelando = false;
      }
    },
    async putTarefa(payload, shouldEmit = true) {
      const response = await axios.put(`/tarefa/${this.sendData.id}`, payload);
      const responseData = response.data.data || response.data;

      if (!shouldEmit) return;

      setTimeout(() => {
        const emittedData = { ...payload, all: responseData };
        const serverComments = emittedData?.all?.TarefaComentario;
        if ((!Array.isArray(serverComments) || serverComments.length === 0) && Array.isArray(this.sendData.TarefaComentario)) {
          emittedData.all.TarefaComentario = this.sendData.TarefaComentario;
        }
        if (Array.isArray(emittedData?.all?.TarefaComentario)) {
          emittedData.all.TarefaComentario.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          this.sendData.TarefaComentario = emittedData.all.TarefaComentario;
        }
        this.$emit("updateEvent", emittedData);
      }, 200);
    },
    async handleSolucionadaAviso() {
      if (!this.sendData?.id) return;
      if (this.normalizeStatus(this.sendData.status) !== "solucionada") return;
      if (this.isSolucionadaPromptOpen) return;

      this.isSolucionadaPromptOpen = true;
      const ultimoComentario = this.getUltimoComentario();
      const mostrarUltimoOperador = ultimoComentario && !this.comentarioEhDoUsuario(ultimoComentario);

      const assinaturaInfo =
        `<p class="mt-2 mb-0 small text-muted">` +
        `Ao confirmar, o chamado sera finalizado e registrado com assinatura digital do seu usuario AD.` +
        `</p>`;
      let html = `<p class="mb-0">A solucao proposta resolveu o problema?</p>${assinaturaInfo}`;
      if (mostrarUltimoOperador && ultimoComentario.comentario) {
        html = `
          <p class="mb-2"><b>Ultimo comentario do operador:</b></p>
          <div class="alert alert-info text-start">${ultimoComentario.comentario}</div>
          <p class="mb-0">A solucao proposta resolveu o problema?</p>
          ${assinaturaInfo}
        `;
      }

      try {
        const result = await Swal.fire({
          icon: "warning",
          title: "Confirmacao de solucao",
          html,
          showDenyButton: true,
          showCancelButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCloseButton: false,
          confirmButtonText: "Sim, resolveu",
          denyButtonText: "Nao resolveu",
        });

        if (result.isConfirmed) {
          this.sendData.status = "Finalizada";
          await this.putTarefa(
            { usuario: 0, status: this.sendData.status, assinaturaAceiteSolucao: true },
            true,
          );
          return;
        }

        // "Nao resolveu": exige assentamento (motivo) antes de reabrir o chamado.
        const motivoResult = await Swal.fire({
          icon: "question",
          title: "Motivo da recusa",
          html: `<p class="mb-2 text-start">Explique por que a solução proposta não resolveu o problema. Essa justificativa será enviada ao técnico responsável.</p>`,
          input: "textarea",
          inputPlaceholder: "Descreva o motivo da recusa...",
          showCancelButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          confirmButtonText: "Confirmar recusa",
          cancelButtonText: "Voltar",
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return "Informe o motivo da recusa para continuar.";
            }
          },
        });

        if (!motivoResult.isConfirmed) {
          return;
        }

        const motivoTexto = motivoResult.value.trim();

        // "Nao resolveu": reabre e envia comentario na mesma requisicao.
        this.sendData.status = "Pendente";

        const comentarioTexto = `${this.user?.nome || "Usuario"} informou que a solucao proposta nao resolveu o problema. Motivo: ${motivoTexto}`;
        const comentario = {
          cId: this.sendData.id + "." + (this.sendData.TarefaComentario ? this.sendData.TarefaComentario.length + 1 : 0),
          tarefa: this.sendData.id,
          comentario: comentarioTexto,
          usuario: 0,
          accountname: this.user.usuario,
          TarefaUsuario: { nome: this.user.nome, id: 0 },
        };

        if (!this.sendData.TarefaComentario) this.sendData.TarefaComentario = [];
        this.sendData.TarefaComentario.push(comentario);
        await this.putTarefa(
          {
            usuario: 0,
            status: this.sendData.status,
            TarefaComentario: [comentario],
          },
          true,
        );
      } catch (error) {
        erro({ title: error.response?.data?.error || error.message || "Erro desconhecido ao validar solucao." });
        console.error("Erro na validacao da solucao:", error.response?.data || error);
      } finally {
        this.isSolucionadaPromptOpen = false;
      }
    },
    async editSubmit() {
      this.submit = true;

      const payload = {};
      let method = "post";
      let url = "/tarefa/";

      payload.usuario = 0;

      let cIdComentarioParaFotos = null;

      if (this.sendData.id) {
        method = "put";
        url = "/tarefa/" + this.sendData.id;
        // Este envio é só comentário (e anexos): o portal não tem editor de
        // situação. Reenviar `sendData.status` devolvia ao backend o status
        // lido quando a tela abriu — se o técnico solucionasse o chamado nesse
        // meio-tempo, o comentário do solicitante rebaixava a situação de volta
        // ("Solucionada" virando "Nova"). O aceite e a recusa da solução têm
        // requisição própria, em `handleSolucionadaAviso`.

        const comentario = {
          cId: this.sendData.id + "." + (this.sendData.TarefaComentario ? this.sendData.TarefaComentario.length + 1 : 0),
          tarefa: this.sendData.id,
          comentario: this.comentario,
          usuario: 0,
          accountname: this.user.usuario,
          TarefaUsuario: { nome: this.user.nome, id: 0 },
        };

        if (this.comentario.trim().length > 0) {
          payload.TarefaComentario = [];
          payload.TarefaComentario.push(comentario);
          if (!this.sendData.TarefaComentario) this.sendData.TarefaComentario = [];
          this.sendData.TarefaComentario.push(comentario);
          this.comentario = "";
          cIdComentarioParaFotos = comentario.cId;
        }
      }

      try {
        const response = await axios[method](url, payload);
        const responseData = response.data.data || response.data;
        if (this.$refs.fotosChamado) {
          await this.$refs.fotosChamado.enviarPendentes(this.sendData.id || responseData.id, cIdComentarioParaFotos);
        }
        setTimeout(() => {
          const emittedData = { ...payload, all: responseData };
          const serverComments = emittedData?.all?.TarefaComentario;
          if ((!Array.isArray(serverComments) || serverComments.length === 0) && Array.isArray(this.sendData.TarefaComentario)) {
            emittedData.all.TarefaComentario = this.sendData.TarefaComentario;
          }
          if (Array.isArray(emittedData?.all?.TarefaComentario)) {
            emittedData.all.TarefaComentario.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            this.sendData.TarefaComentario = emittedData.all.TarefaComentario;
          }
          this.$emit("updateEvent", emittedData);
        }, 200);
      } catch (error) {
        erro({ title: error.response?.data?.error || error.message || "Erro desconhecido ao salvar tarefa." });
        console.error("Erro na requisicao:", error.response?.data || error);
      } finally {
        this.submit = false;
      }
    },
    async createSubtask() {
      const subtask = { ...this.subtask, status: "pendente" };

      if (this.sendData.id) {
        subtask.tarefa = this.sendData.id;
        axios
          .post(`/tarefa/subtarefa`, subtask)
          .then((response) => {
            if (!response.error) {
              if (!this.sendData.TarefaSubtarefa) this.sendData.TarefaSubtarefa = [];
              this.sendData.TarefaSubtarefa.push(response.data);
              this.subtask = { nome: "", descricao: "" };
              this.modalShow = false;
            }
          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        if (!this.sendData.TarefaSubtarefa) this.sendData.TarefaSubtarefa = [];
        this.sendData.TarefaSubtarefa.push(subtask);
        this.subtask = { nome: "", descricao: "" };
        this.modalShow = false;
      }
    },
  },
};
</script>
